import React, { useState, useEffect, FC } from 'react';
import { connect } from 'react-redux';

import DoubleGisMap from '@components/GisMap';
import AllOffices from '@components/AllOffices';

import getOfficesMarkers from '@helpers/getOfficesMarkers';
import useDeviceType from '@hooks/useDeviceType';

import { onSelectMfc } from '@store/actions';
import {
  structuredSelector,
  StructuredSelectorProps,
} from './structuredSelector';

import st from './MapWrapper.module.css';

// Документация - https://bitbucket.org/mosru/mos-cat/src/master/packages/components/presentational/GisMap/

interface MapWrapperProps extends StructuredSelectorProps {
  offices?: [number];
  undergrounds?: [any];
  selectedMarker?: any;
  setSelectedMarker?: (office) => any;
  onSelectMfc?: (payload) => any;
  onActivePointClick?: () => void;
  withRating: boolean;
  isDetailed?: boolean;
  mapOptions?: {
    zoomControl: boolean;
    fullscreenControl: boolean;
    locationControl: boolean;
  };
  reset?: () => void;
  isFilterApplied?: boolean;
  showingNearest?: boolean;
  mobileMapOpened?: boolean;
  headerHeight?: number;
}

const getHeight = (desktop, mobileMapOpened, headerHeight, isDetailed) => {
  if ((!desktop && mobileMapOpened) || !isDetailed) {
    return `100%`;
  }
  return `calc(100vh - ${headerHeight}px)`;
};

const MapWrapper: FC<MapWrapperProps> = ({
  offices = [],
  undergrounds,
  setSelectedMarker = () => {},
  selectedMarker,
  withRating,
  mapOptions = {
    zoomControl: true,
    fullscreenControl: false,
    locationControl: true,
  },
  routeSelected,
  onSelectMfc = () => {},
  isDetailed = false,
  mobileMapOpened,
  headerHeight,
  reset,
  legendOpened,
  isFilterApplied = false,
  onActivePointClick,
  showingNearest = false,
}) => {
  const [wide, setWide] = useState(false);
  const { phone, desktop } = useDeviceType();

  useEffect(() => {
    if (!legendOpened) {
      setWide(true);
    }
  }, [legendOpened]);
  const routeGeoJSON = routeSelected?.routeGeoJSON;
  const startPoint = routeSelected?.startPoint;
  const routeStyleHide = routeSelected?.routeStyleHide;

  const markersClassNames = {
    happy: 'markerHappy',
    indifferent: 'markerIndifferent',
    sad: 'markerSad',
    closed: 'markerGrey',
  };

  const handlePointClick = (office) => {
    if (office[0].className === 'routeStartPoint') return;
    if (office && selectedMarker && office[0].id === selectedMarker.id) {
      onActivePointClick();
    }
    setSelectedMarker(office[0]);
    onSelectMfc(office[0]);
  };

  const points = getOfficesMarkers(
    markersClassNames,
    offices,
    undergrounds,
    isDetailed && selectedMarker?.id
  );

  if (startPoint) {
    points.push(startPoint);
  }

  const selectedMarkerClass = !selectedMarker ? '' : selectedMarker.className;
  const mapRatingClass = withRating
    ? `${st.mapWithRating} ${selectedMarkerClass}Active`
    : '';
  const officeStatus = !selectedMarker ? '' : selectedMarker.officeStatus;
  const officeClose = officeStatus === 'CLOSE';

  const mapClassNames = {
    default: st.map,
    rating: mapRatingClass,
    closed: `${st.map} mapGreyActive`,
    ratingAndClosed: `${st.mapWithRating} mapGreyActive`,
  };

  const getMapClass = (classNames) => {
    if (officeClose && withRating) {
      return classNames.ratingAndClosed;
    }
    if (officeClose) {
      return classNames.closed;
    }
    if (withRating) {
      return classNames.rating;
    }
    return classNames.default;
  };

  const mapClass = (classNames) => {
    const mainClass = getMapClass(classNames);

    if (isDetailed) {
      if (legendOpened) {
        return st.detailedWide;
      }
      if (officeClose) {
        return st.detailedClose;
      }
      return st.detailed;
    }

    return mainClass;
  };

  const setMapClass = mapClass(mapClassNames);

  const defaultZoomForDetailed = legendOpened || wide;
  const defaultZoomForMain = !isFilterApplied;
  const isDefaultZoom = isDetailed
    ? defaultZoomForDetailed
    : defaultZoomForMain;

  const defaultZoom = () => {
    if (routeGeoJSON) {
      return undefined;
    }
    if (isDefaultZoom) {
      if (phone) {
        return 10;
      }
      return 11;
    }
    return 11;
    // return isDefaultZoom && !routeGeoJSON ? 11 : undefined;
  };

  const isDefaultCenter = selectedMarker || isFilterApplied;
  const defaultCenter = isDefaultCenter
    ? undefined
    : { lat: 55.751288, lng: 37.617928 };

  const key = `${legendOpened}_${isDetailed}`;

  return (
    <div
      className={setMapClass}
      style={{
        top: headerHeight,
        height: getHeight(desktop, mobileMapOpened, headerHeight, isDetailed),
      }}
    >
      <DoubleGisMap
        key={key}
        zoomIfSelected={isDetailed || phone}
        centerIfSelected={isDetailed || phone || showingNearest}
        mapOptions={{
          ...mapOptions,
        }}
        moscowViewOnInit
        defaultZoom={defaultZoom()}
        defaultCenter={defaultCenter}
        clustering={false}
        points={points}
        className={st.gisMap}
        selectedPoints={selectedMarker ? [selectedMarker] : []}
        onPointClick={handlePointClick}
        routeGeoJSON={routeGeoJSON}
        isPhone={phone}
        condensedMode
        routeStyleHide={routeStyleHide}
      />
      {isDetailed && <AllOffices onClick={reset} visible={desktop} />}
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  onSelectMfc: (payload) => dispatch(onSelectMfc(payload)),
});

export default connect(structuredSelector, mapDispatchToProps)(MapWrapper);
